<template>
    <defaultSec title="微信渠道月报" class="wechat-monthly-report">
        <div class="report-page">
            <section class="report-toolbar">
                <div class="toolbar-copy">
                    <strong>按月查看微信渠道经营数据</strong>
                    <span>选择月份后可查看汇总与员工券订单明细，并导出完整 Excel。</span>
                </div>
                <el-form :inline="true" size="small" class="month-form" @submit.native.prevent>
                    <el-form-item label="数据月份">
                        <el-date-picker
                            v-model="selectedMonth"
                            type="month"
                            value-format="yyyy-MM"
                            format="yyyy 年 MM 月"
                            placeholder="选择月份"
                            :clearable="false"
                            :picker-options="monthPickerOptions"
                            @keyup.enter.native="queryReport">
                        </el-date-picker>
                    </el-form-item>
                    <el-form-item>
                        <el-button
                            type="primary"
                            icon="el-icon-search"
                            :loading="loading"
                            @click="queryReport">
                            查询
                        </el-button>
                        <el-button
                            icon="el-icon-download"
                            :disabled="!loadedMonth || selectedMonth !== loadedMonth || loading"
                            :loading="exporting"
                            @click="exportReport">
                            导出 Excel
                        </el-button>
                    </el-form-item>
                </el-form>
            </section>

            <el-alert
                v-if="isCurrentMonth"
                class="month-notice"
                title="当前月份数据仍在持续更新，月末前导出的结果不是完整月报。"
                type="warning"
                :closable="false"
                show-icon>
            </el-alert>

            <div v-if="loadError" class="state-panel">
                <i class="el-icon-warning-outline"></i>
                <strong>月报数据加载失败</strong>
                <span>{{ loadError }}</span>
                <el-button size="small" type="primary" plain @click="queryReport">重新加载</el-button>
            </div>

            <template v-else>
                <section class="summary-section" v-loading="summaryLoading" element-loading-text="正在统计月度数据...">
                    <div class="section-heading">
                        <div>
                            <strong>月度汇总</strong>
                            <span v-if="loadedMonth">{{ monthLabel(loadedMonth) }}，有效订单状态 3–19</span>
                        </div>
                        <el-tag v-if="loadedMonth" size="small" type="info">统计完成</el-tag>
                    </div>

                    <div v-if="summary" class="metric-strip">
                        <div v-for="metric in summaryMetrics" :key="metric.key" class="metric-group">
                            <div class="metric-title">
                                <i :class="metric.icon"></i>
                                <span>{{ metric.label }}</span>
                            </div>
                            <div class="metric-values">
                                <div>
                                    <strong>{{ formatInteger(metric.count) }}</strong>
                                    <span>订单数</span>
                                </div>
                                <div>
                                    <strong>¥ {{ formatMoney(metric.amount) }}</strong>
                                    <span>{{ metric.amountLabel }}</span>
                                </div>
                            </div>
                        </div>
                    </div>
                    <el-empty v-else-if="!summaryLoading" description="请选择月份查询月报数据"></el-empty>
                </section>

                <section class="detail-section">
                    <div class="section-heading detail-heading">
                        <div>
                            <strong>员工券订单明细</strong>
                            <span>按“人力部门优惠券”分类识别，共 {{ formatInteger(detailTotal) }} 条券支付记录</span>
                        </div>
                        <span class="detail-hint">同一订单使用多张员工券时会显示多条记录</span>
                    </div>

                    <el-table
                        :data="detailRows"
                        border
                        size="mini"
                        v-loading="detailLoading"
                        element-loading-text="正在加载员工券订单..."
                        height="calc(100vh - 455px)"
                        min-height="280">
                        <el-table-column prop="orderMainNo" label="主订单号" min-width="190" fixed="left">
                            <template slot-scope="scope"><span class="mono">{{ scope.row.orderMainNo }}</span></template>
                        </el-table-column>
                        <el-table-column prop="customerId" label="客户 ID" min-width="180">
                            <template slot-scope="scope"><span class="mono">{{ scope.row.customerId }}</span></template>
                        </el-table-column>
                        <el-table-column prop="payTime" label="下单时间" width="160"></el-table-column>
                        <el-table-column prop="orderPayAmount" label="订单实付" width="110" align="right">
                            <template slot-scope="scope">¥ {{ formatMoney(scope.row.orderPayAmount) }}</template>
                        </el-table-column>
                        <el-table-column prop="employeeCouponDeductionAmount" label="员工券抵扣" width="120" align="right">
                            <template slot-scope="scope"><strong class="deduction">¥ {{ formatMoney(scope.row.employeeCouponDeductionAmount) }}</strong></template>
                        </el-table-column>
                        <el-table-column prop="ticketBatchName" label="券批次" min-width="220" show-overflow-tooltip></el-table-column>
                        <el-table-column prop="ticketAmount" label="券面额" width="100" align="right">
                            <template slot-scope="scope">¥ {{ formatMoney(scope.row.ticketAmount) }}</template>
                        </el-table-column>
                        <el-table-column prop="ticketCustomerId" label="客户券 ID" width="120">
                            <template slot-scope="scope"><span class="mono">{{ scope.row.ticketCustomerId }}</span></template>
                        </el-table-column>
                        <el-table-column prop="ticketBatchId" label="券批次 ID" width="130">
                            <template slot-scope="scope"><span class="mono">{{ scope.row.ticketBatchId }}</span></template>
                        </el-table-column>
                        <el-table-column prop="ticketClass" label="券分类" width="85" align="center"></el-table-column>
                        <el-table-column prop="usedTime" label="使用时间" width="160"></el-table-column>
                        <div slot="empty" class="table-empty">
                            <i class="el-icon-tickets"></i>
                            <span>{{ loadedMonth ? '该月份没有员工券订单' : '查询后显示员工券订单明细' }}</span>
                        </div>
                    </el-table>

                    <div v-if="detailTotal > 0" class="pagination-wrap">
                        <el-pagination
                            background
                            layout="total, sizes, prev, pager, next, jumper"
                            :current-page="currentPage"
                            :page-size="pageSize"
                            :page-sizes="[20, 50, 100]"
                            :total="detailTotal"
                            @size-change="changePageSize"
                            @current-change="changePage">
                        </el-pagination>
                    </div>
                </section>

                <section class="definition-bar">
                    <strong>统计口径</strong>
                    <span>微信全部订单：CHANNEL_ID = 86</span>
                    <span>推文订单：NCP_BAT = Jc5n6qfS</span>
                    <span>私域订单：STATUS = 1，按 ORDER_NO 去重</span>
                    <span>员工券：人力部门优惠券分类</span>
                </section>
            </template>
        </div>
    </defaultSec>
</template>

<script>
import defaultSec from '@c/defaultSection'
import {baseUrl} from '@/api/http'

const API = 'lechun-cms/orderReport/wechatChannelMonthly'

export default {
    name: 'wechatChannelMonthlyReport',
    components: {defaultSec},
    data() {
        return {
            selectedMonth: this.previousMonth(),
            loadedMonth: '',
            summary: null,
            detailRows: [],
            detailTotal: 0,
            currentPage: 1,
            pageSize: 20,
            summaryLoading: false,
            detailLoading: false,
            exporting: false,
            loadError: '',
            monthPickerOptions: {
                disabledDate(time) {
                    const now = new Date()
                    return time.getFullYear() > now.getFullYear()
                        || (time.getFullYear() === now.getFullYear() && time.getMonth() > now.getMonth())
                }
            }
        }
    },
    computed: {
        loading() {
            return this.summaryLoading || this.detailLoading
        },
        isCurrentMonth() {
            return this.selectedMonth === this.formatMonth(new Date())
        },
        summaryMetrics() {
            if (!this.summary) return []
            return [
                {key: 'wechat', label: '微信全部订单', icon: 'el-icon-shopping-bag-1', count: this.summary.wechatOrderCount, amount: this.summary.wechatOrderAmount, amountLabel: '订单金额'},
                {key: 'tweet', label: '推文订单', icon: 'el-icon-document', count: this.summary.tweetOrderCount, amount: this.summary.tweetOrderAmount, amountLabel: '订单金额'},
                {key: 'private', label: '私域订单', icon: 'el-icon-connection', count: this.summary.privateOrderCount, amount: this.summary.privateOrderAmount, amountLabel: '订单金额'},
                {key: 'coupon', label: '员工券订单', icon: 'el-icon-bank-card', count: this.summary.employeeCouponOrderCount, amount: this.summary.employeeCouponDeductionAmount, amountLabel: '抵扣金额'}
            ]
        }
    },
    mounted() {
        this.queryReport()
    },
    methods: {
        queryReport() {
            if (!this.selectedMonth) {
                this.$message.warning('请选择数据月份')
                return
            }
            this.currentPage = 1
            this.loadError = ''
            this.summaryLoading = true
            this.detailLoading = true
            const month = this.selectedMonth
            return Promise.all([
                this.$apiGet(`${API}/summary`, {month}).then(result => {
                    this.summary = result || null
                }),
                this.fetchDetails(month)
            ]).then(() => {
                this.loadedMonth = month
            }).catch(error => {
                this.summary = null
                this.detailRows = []
                this.detailTotal = 0
                this.loadedMonth = ''
                this.loadError = (error && error.message) || '请检查网络后重试'
            }).finally(() => {
                this.summaryLoading = false
                this.detailLoading = false
            })
        },
        fetchDetails(month) {
            this.detailLoading = true
            return this.$apiGet(`${API}/employeeCouponOrders`, {
                month,
                currentPage: this.currentPage,
                pageSize: this.pageSize
            }).then(result => {
                const page = result || {}
                this.detailRows = page.list || []
                this.detailTotal = Number(page.total || 0)
            }).finally(() => {
                this.detailLoading = false
            })
        },
        changePage(page) {
            this.currentPage = page
            this.fetchDetails(this.loadedMonth).catch(error => {
                this.$message.error((error && error.message) || '员工券订单加载失败')
            })
        },
        changePageSize(size) {
            this.pageSize = size
            this.currentPage = 1
            this.changePage(1)
        },
        exportReport() {
            if (!this.loadedMonth || this.selectedMonth !== this.loadedMonth || this.exporting) return
            this.exporting = true
            const root = (baseUrl || '').replace(/\/$/, '')
            const url = `${root}/${API}/export?month=${encodeURIComponent(this.loadedMonth)}&pageRouterUrl=wechatChannelMonthlyReport`
            const exportWindow = window.open(url, '_blank')
            if (!exportWindow) {
                this.$message.warning('浏览器阻止了下载窗口，请允许弹窗后重试')
            } else {
                this.$message.success(`${this.monthLabel(this.loadedMonth)}月报已开始导出`)
            }
            window.setTimeout(() => { this.exporting = false }, 800)
        },
        previousMonth() {
            const date = new Date()
            date.setDate(1)
            date.setMonth(date.getMonth() - 1)
            return this.formatMonth(date)
        },
        formatMonth(date) {
            return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`
        },
        monthLabel(month) {
            if (!month) return ''
            const parts = month.split('-')
            return `${parts[0]} 年 ${Number(parts[1])} 月`
        },
        formatInteger(value) {
            const number = Number(value || 0)
            return Number.isFinite(number) ? number.toLocaleString('zh-CN') : '0'
        },
        formatMoney(value) {
            const number = Number(value || 0)
            return Number.isFinite(number)
                ? number.toLocaleString('zh-CN', {minimumFractionDigits: 2, maximumFractionDigits: 2})
                : '0.00'
        }
    }
}
</script>

<style scoped lang="scss">
.wechat-monthly-report {
    --report-primary: #209e91;
    --report-text: #27303f;
    --report-muted: #7d8796;
    --report-border: #e4e8ee;
    --report-surface: #f7f9fb;
}

.report-page {
    min-width: 920px;
    color: var(--report-text);
}

.report-toolbar {
    min-height: 72px;
    padding: 14px 18px;
    border: 1px solid var(--report-border);
    background: var(--report-surface);
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 24px;
    box-sizing: border-box;
}

.toolbar-copy {
    display: flex;
    flex-direction: column;
    gap: 5px;

    strong { font-size: 15px; }
    span { color: var(--report-muted); font-size: 12px; }
}

.month-form {
    flex: none;

    ::v-deep .el-form-item { margin-bottom: 0; }
    ::v-deep .el-date-editor { width: 176px; }
}

.month-notice { margin-top: 10px; }

.summary-section,
.detail-section {
    margin-top: 12px;
    border: 1px solid var(--report-border);
    background: #fff;
}

.section-heading {
    min-height: 49px;
    padding: 0 16px;
    border-bottom: 1px solid var(--report-border);
    display: flex;
    align-items: center;
    justify-content: space-between;
    box-sizing: border-box;

    > div {
        display: flex;
        align-items: baseline;
        gap: 10px;
    }

    strong { font-size: 14px; }
    span { color: var(--report-muted); font-size: 12px; }
}

.metric-strip {
    display: grid;
    grid-template-columns: repeat(4, minmax(190px, 1fr));
}

.metric-group {
    min-height: 112px;
    padding: 15px 16px;
    border-right: 1px solid var(--report-border);
    box-sizing: border-box;

    &:last-child { border-right: 0; }
}

.metric-title {
    display: flex;
    align-items: center;
    gap: 7px;
    color: #505a69;
    font-size: 12px;

    i { color: var(--report-primary); font-size: 15px; }
}

.metric-values {
    margin-top: 14px;
    display: grid;
    grid-template-columns: minmax(72px, .8fr) minmax(110px, 1.2fr);
    gap: 12px;

    > div { display: flex; flex-direction: column; gap: 6px; }
    strong { font-size: 20px; line-height: 1; white-space: nowrap; }
    span { color: var(--report-muted); font-size: 11px; }
}

.detail-heading { border-bottom: 0; }
.detail-hint { white-space: nowrap; }

.mono {
    font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
    font-size: 11px;
}

.deduction { color: #b36a17; font-weight: 600; }

.table-empty {
    min-height: 116px;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 8px;
    color: #9aa3af;

    i { font-size: 27px; }
}

.pagination-wrap {
    padding: 12px 16px;
    border-top: 1px solid var(--report-border);
    text-align: right;
}

.definition-bar {
    margin-top: 10px;
    padding: 10px 14px;
    border: 1px solid #dfe6e5;
    background: #f7faf9;
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 8px 20px;
    color: #68737f;
    font-size: 11px;

    strong { color: #3f4a56; }
}

.state-panel {
    min-height: 260px;
    margin-top: 12px;
    border: 1px solid var(--report-border);
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 10px;
    color: var(--report-muted);

    i { color: #d98b3a; font-size: 32px; }
    strong { color: var(--report-text); font-size: 15px; }
    span { font-size: 12px; }
}

@media (max-width: 1180px) {
    .report-toolbar { align-items: flex-start; flex-direction: column; gap: 12px; }
    .metric-strip { grid-template-columns: repeat(2, 1fr); }
    .metric-group:nth-child(2) { border-right: 0; }
    .metric-group:nth-child(-n+2) { border-bottom: 1px solid var(--report-border); }
}
</style>
